import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Constant } from '../common/constant';

export interface VwHelperState {
  helperKey: string;
  seen: boolean;
  dontShowAgain: boolean;
  lastModifiedDate?: string;
}

export interface VwHelperStateList {
  firstVisit: boolean;
  states: VwHelperState[];
}

// Per-user "has this user seen in-app helper X" state (tooltips, the guided
// tour, etc). Backed by GET/POST /api/helper_state/* — every call there is
// scoped server-side to the JWT's own user, never a user id we send.
@Injectable({ providedIn: 'root' })
export class HelperStateService {
  private stateMap = new Map<string, VwHelperState>();
  private firstVisitFlag = false;
  private loadedSubject = new BehaviorSubject<boolean>(false);
  loaded$ = this.loadedSubject.asObservable();

  constructor(private readonly http: HttpClient) {}

  isLoaded(): boolean {
    return this.loadedSubject.value;
  }

  load(): Observable<VwHelperStateList> {
    const url = Constant.REST_URL + Constant.HELPER_STATE + Constant.GET_ALL_HELPER_STATE;
    return this.http.get<VwHelperStateList>(url).pipe(
      tap(result => {
        this.stateMap.clear();
        (result.states || []).forEach(s => this.stateMap.set(s.helperKey, s));
        this.firstVisitFlag = !!result.firstVisit;
        this.loadedSubject.next(true);
      })
    );
  }

  isFirstVisit(): boolean {
    return this.firstVisitFlag;
  }

  // True once the user has either seen it, or asked never to see it again.
  isDismissed(helperKey: string): boolean {
    const s = this.stateMap.get(helperKey);
    return !!s && (s.seen || s.dontShowAgain);
  }

  isDontShowAgain(helperKey: string): boolean {
    const s = this.stateMap.get(helperKey);
    return !!s && s.dontShowAgain;
  }

  // Tips the user previously dismissed with "don't show again" — the Help
  // panel offers to bring these back on demand.
  getDontShowAgainKeys(): string[] {
    return Array.from(this.stateMap.values())
      .filter(s => s.dontShowAgain)
      .map(s => s.helperKey);
  }

  markSeen(helperKey: string, dontShowAgain: boolean = false): Observable<VwHelperState> {
    const url = Constant.REST_URL + Constant.HELPER_STATE + Constant.MARK_HELPER_SEEN;
    return this.http.post<VwHelperState>(url, { helperKey, dontShowAgain }).pipe(
      tap(result => {
        this.stateMap.set(result.helperKey, result);
        // firstVisitFlag deliberately isn't cleared here — it reflects "this
        // account had zero helper-state rows when this session started",
        // which stays true for the user's whole first session (so every new
        // page they visit for the first time still gets its auto-tour), not
        // just up until they dismiss the very first one. It naturally
        // becomes false on their next login, once load() re-queries and
        // finds rows.
      })
    );
  }

  reset(helperKey: string): Observable<void> {
    const url = Constant.REST_URL + Constant.HELPER_STATE + Constant.RESET_HELPER_STATE;
    return this.http.post<void>(url, { helperKey }).pipe(tap(() => this.stateMap.delete(helperKey)));
  }

  resetAll(): Observable<void> {
    const url = Constant.REST_URL + Constant.HELPER_STATE + Constant.RESET_ALL_HELPER_STATE;
    return this.http.post<void>(url, {}).pipe(
      tap(() => {
        this.stateMap.clear();
        this.firstVisitFlag = true;
      })
    );
  }

  // Called on logout so the next login (possibly a different user) doesn't
  // start with a stale cache.
  clearLocalCache(): void {
    this.stateMap.clear();
    this.firstVisitFlag = false;
    this.loadedSubject.next(false);
  }
}
